// Home, laid out like the Epic web store (no rail: see main.ts) with the launcher's Home sections: a 16:9 hero with
// the featured list beside it (its active row filling as the slide's time runs), one poster row, a wide
// spotlight, then the other rows as three compact columns. On a phone the list folds into dots under the hero and
// the rows scroll sideways.
import { comingSoonText, getCatalogue, getHome, isComingSoon, KIND_LABEL, label, warmCatalogue, type Game, type HomeCatalogue, type Kind } from "../catalogue.ts";
import { coverStyle, creatorName, lazyCover, newBadge, playActions, titleArt, typedCover } from "../components.ts";
import { clear, h, link } from "../dom.ts";
import { icon } from "../icons.ts";
import { setTitle } from "../router.ts";
import { artRank, coverUrl, HALLOWEEN_ROW, homeRows, rowHref, type HomeRow } from "../rows.ts";
import { withPortals } from "../zones/portals.ts";
import { creatorsYouFollow } from "../zones/creators/index.ts";
import { signedIn } from "../session.ts";
import { plazaCard } from "../plaza.ts";
import { featuredStrip } from "../zones/category/index.ts";

/** seconds each slide stays up and the slide change's length: the launcher's HERO_SECS and TRANS_SECS */
const SLIDE_MS = 8000;
const TRANS_MS = 1150;

/** The last hero this browser saw, so a return visit paints its poster before the catalogue arrives. */
const HERO_KEY = "banger.home.hero";
type HeroPeek = { title: string; art: string };

function peekHero(): HeroPeek | null {
  try {
    const v = JSON.parse(localStorage.getItem(HERO_KEY) ?? "null") as HeroPeek | null;
    return v && typeof v.title === "string" && typeof v.art === "string" && /^\/art\/[\w.-]+$/.test(v.art) ? v : null;
  } catch {
    return null;
  }
}

function keepHero(g: Game): void {
  const art = coverUrl(g, "full");
  try {
    if (art && art.startsWith("/art/")) localStorage.setItem(HERO_KEY, JSON.stringify({ title: g.title, art }));
  } catch {
    /* private window: no cache, the skeleton is plain */
  }
}

/** Home's frame before the catalogue arrives: the hero (last visit's poster when we have it) and two shelves. */
function skeleton(): HTMLElement {
  const peek = peekHero();
  const shelf = () => h("div", { class: "skel-shelf", "aria-hidden": "true" }, h("span", { class: "skel-line" }), h("div", { class: "skel-track" }, Array.from({ length: 6 }, () => h("span", { class: "skel-poster" }))));
  return h(
    "div",
    { class: "home-skeleton", "aria-busy": "true", "aria-label": "Loading" },
    h("div", { class: "skel-hero", style: peek ? { backgroundImage: `url("${peek.art}")` } : null }, peek ? h("span", { class: "skel-hero-title" }, peek.title) : null),
    shelf(),
    shelf(),
  );
}

/** The page main.ts painted before the router started, which the router's first render reuses. */
let prerendered: HTMLElement | null = null;

/** Paint Home into `outlet` now, before the session loads; the router's first render of `/` takes this same page. */
export function prerenderHome(outlet: HTMLElement): void {
  prerendered = buildHome();
  outlet.replaceChildren(prerendered);
}

/**
 * Home paints at once: the skeleton now, the hero and rows when the slim home payload arrives (wave 12: 2–3 s of
 * blank screen behind the full catalogue). The router puts the page up as soon as this returns.
 */
export function homePage(): HTMLElement {
  setTitle(null);
  const pre = prerendered;
  prerendered = null;
  // a first visit to `/` builds Home once, not twice (fixes.md handover)
  return pre?.isConnected ? pre : buildHome();
}

function buildHome(): HTMLElement {
  const root = h("div", { class: "home" }, skeleton());
  void getHome().then(
    (cat) => {
      clear(root, homeParts(cat));
      warmCatalogue(); // Browse and search want the full catalogue: fetch it while the player looks at Home
    },
    (e) => clear(root, h("div", { class: "empty" }, h("h1", null, "Something went wrong"), h("p", { class: "dim" }, e instanceof Error ? e.message : String(e)), h("a", { class: "btn", href: "/" }, "Try again"))),
  );
  return root;
}

function homeParts(cat: HomeCatalogue): (HTMLElement | null)[] {
  // wave 11: the catalogue's resolved rows (rule rows, seasons, the new-game boost); `lists` on an older accounts
  const all = homeRows(cat);
  const hero = all.find((r) => r.style === "hero");
  const featured = hero?.games.length ? hero.games : cat.games.slice(0, 5);
  if (featured[0]) keepHero(featured[0]);
  const rest = all.filter((r) => r.style !== "hero");
  const spot = rest.find((r) => r.style === "spotlight");
  const rows = rest.filter((r) => r.style !== "spotlight");
  // the Open Haunt row (first during the season) always gets a full poster row, never a compact column
  const [first0, ...others0] = rows;
  const columns0 = others0.length > 3 ? others0.slice(0, 3).filter((r) => r.id !== HALLOWEEN_ROW) : [];
  const spotGames = spot ? spotlightPick(spot.games, featured, rows) : [];
  // each game once down the page (design review 19 Oct): rows skip what the hero, the spotlight and earlier rows drew
  const drawn = [first0, ...columns0, ...others0.filter((r) => !columns0.includes(r))].filter((r): r is HomeRow => !!r);
  const deduped = dedupeRows(drawn, [...featured, ...spotGames.slice(0, 1)], (r) => (columns0.includes(r) ? COMPACT_SHOWN : POSTER_MIN));
  const pick = (r: HomeRow | undefined) => (r ? deduped.get(r) ?? r : undefined);
  const first = pick(first0);
  const columns = columns0.map((r) => pick(r)!);
  const extra = others0.filter((r) => !columns0.includes(r)).map((r) => pick(r)!);
  const firstRow = first ? posterRow(first) : null;
  const columnsEl = columns.length ? h("div", { class: "columns" }, columns.map((l) => compactColumn(l))) : null;
  const blocks = withPortals(
    carousel(featured, hero?.boosted),
    [
      firstRow,
      spot ? spotlight(spotGames) : null,
      columnsEl,
      ...extra.map((l) => posterRow(l)),
    ],
    cat,
  );
  // creators you follow (the creators zone's row), right under the hero and its portal; it isn't counted as a row for
  // portal slots, and stays out of the page until it has something to show
  const following = followingSlot();
  const at = blocks.findIndex((b, i) => i > 0 && !b.classList.contains("home-portal"));
  if (following) blocks.splice(at < 0 ? blocks.length : at, 0, following);
  // the Plaza, always open (wave 13: join at a glance): after the compact columns, so it never sits on the spotlight;
  // without them, after the first row
  const anchor = [columnsEl, firstRow].map((b) => (b ? blocks.indexOf(b) : -1)).find((i) => i >= 0) ?? -1;
  const plaza = plazaCard();
  blocks.splice(anchor < 0 ? Math.min(1, blocks.length) : anchor + 1, 0, plaza);
  // the featured worlds (Japanese, Gamepad, All ages…) under the Plaza
  const feat = featuredStrip();
  if (feat) blocks.splice(blocks.indexOf(plaza) + 1, 0, feat);
  // the launch trailer, right under the hero
  blocks.splice(1, 0, trailer());
  return [...blocks, pitch(), kinds(cat)];
}

/** The launch trailer (owner, 2026-11-04): served by nginx from the env's art folder, nothing loads until play. */
export const TRAILER = { src: "/art/trailer/banger-trailer.mp4", poster: "/art/trailer/banger-trailer.jpg" };

function trailer(): HTMLElement {
  return h(
    "section",
    { class: "trailer", "aria-label": "The Banger trailer" },
    h("div", { class: "trailer-head" }, h("p", { class: "eyebrow" }, "Trailer"), h("h2", null, "Gaming changed forever")),
    h("video", { class: "trailer-video", src: TRAILER.src, poster: TRAILER.poster, controls: true, preload: "none", playsinline: true }),
  );
}

/** Poster rows keep at least this many games after dedupe (refilled with repeats when short). */
const POSTER_MIN = 6;
/** A compact column shows five games. */
const COMPACT_SHOWN = 5;
/** Games a poster row draws (the /home payload trims rows to 12). */
const POSTER_SHOWN = 12;

/**
 * Rows without games already drawn above them: the hero's and the spotlight's games, then each earlier row's. A row
 * that would fall under its `min` takes back its own repeats, in its own order, up to the min. Returns the rows that
 * changed, keyed by the original row.
 */
export function dedupeRows(rows: HomeRow[], above: Pick<Game, "id">[], min: (r: HomeRow) => number): Map<HomeRow, HomeRow> {
  const seen = new Set(above.map((g) => g.id));
  const out = new Map<HomeRow, HomeRow>();
  for (const r of rows) {
    const want = min(r);
    const fresh = r.games.filter((g) => !seen.has(g.id));
    let games = fresh;
    if (fresh.length < Math.min(want, r.games.length)) {
      const keep = new Set(fresh.map((g) => g.id));
      let room = want - fresh.length;
      games = r.games.filter((g) => keep.has(g.id) || (room-- > 0 && seen.has(g.id)));
    }
    const shown = games.slice(0, r === rows[0] || want === POSTER_MIN ? POSTER_SHOWN : want);
    for (const g of shown) seen.add(g.id);
    if (games.length !== r.games.length) out.set(r, { ...r, games });
  }
  return out;
}

/** A hidden slot that becomes "Creators you follow" for a signed-in player who follows someone; null signed out. */
function followingSlot(): HTMLElement | null {
  if (!signedIn()) return null;
  const slot = h("div", { class: "home-following", hidden: true });
  // followed creators can have games outside the slim payload, so this row reads the full catalogue
  void getCatalogue()
    .then((full) => creatorsYouFollow(full))
    .then((el) => el && slot.isConnected && slot.replaceWith(el), () => {});
  return slot;
}

/** The launcher's runtime badge: WEB outlined on dark glass, the app runtime solid white. */
export function runtimeBadge(g: Pick<Game, "runtime">): HTMLElement {
  const web = g.runtime === "web";
  return h("span", { class: web ? "rt-badge" : "rt-badge solid" }, web ? "Web" : "App");
}

/** A portrait cover card (launcher `card`: 196×262, radius 12, badge top left, title over the art's foot). */
export function posterCard(g: Game, boosted?: Set<string>): HTMLElement {
  return h(
    "li",
    { class: "poster" },
    link(
      `/g/${g.id}`,
      { class: "poster-link", "aria-label": `${g.title} by ${creatorName(g.creator.name)}` },
      h(
        "span",
        { class: "poster-art", ...lazyCover(g) },
        runtimeBadge(g),
        isComingSoon(g) ? h("span", { class: "badge warn poster-soon" }, comingSoonText(g)) : newBadge(g, boosted, "badge new poster-soon"),
        // the title shows once: under the poster, or over the gradient when there's no art
        coverUrl(g) ? null : h("span", { class: "poster-title", "aria-hidden": "true" }, g.title),
      ),
      coverUrl(g) && !typedCover(g) ? h("span", { class: "poster-name" }, g.title) : null,
      h("span", { class: "poster-by" }, creatorName(g.creator.name)),
    ),
  );
}

function posterRow(l: HomeRow): HTMLElement | null {
  const list = l.games;
  if (list.length === 0) return null;
  const track = h("ul", { class: "poster-track", role: "list" }, list.map((g) => posterCard(g, l.boosted)));
  const page = (dir: number) =>
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  return h(
    "section",
    { class: `shelf${l.id === HALLOWEEN_ROW ? " haunt-shelf" : ""}`, "aria-label": l.title },
    h(
      "div",
      { class: "row-head" },
      link(rowHref(l.id), { class: "row-title" }, h("h2", null, l.title), icon("right", 16)),
      h(
        "div",
        { class: "row-arrows" },
        h("button", { type: "button", class: "round", "aria-label": `Scroll ${l.title} back`, onclick: () => page(-1) }, icon("left", 16)),
        h("button", { type: "button", class: "round", "aria-label": `Scroll ${l.title} on`, onclick: () => page(1) }, icon("right", 16)),
      ),
    ),
    track,
  );
}

/**
 * The spotlight's game: the row's first that isn't already a hero slide (the hero and the spotlight showing the same
 * game reads as a mistake), else the first game with real art from the other rows that isn't one; none hides it.
 */
export function spotlightPick(spot: Game[], featured: Game[], rows: { games: Game[] }[]): Game[] {
  const shown = new Set(featured.map((g) => g.id));
  const fresh = spot.filter((g) => !shown.has(g.id));
  if (fresh.length) return fresh;
  const other = rows.flatMap((r) => r.games).find((g) => !shown.has(g.id) && artRank(g) === 0);
  return other ? [other] : [];
}

function spotlight(list: Game[]): HTMLElement | null {
  const g = list[0];
  if (!g) return null;
  return h(
    "article",
    { class: "spotlight", style: coverStyle(g, "full") },
    h(
      "div",
      { class: "spotlight-text" },
      h("p", { class: "eyebrow" }, `Spotlight · ${label(g.genre)}`),
      titleArt(g, "spotlight-title-art", () => h("h2", { class: "spotlight-title" }, link(`/g/${g.id}`, null, g.title))),
      h("p", { class: "spotlight-blurb" }, g.blurb),
      h("div", { class: "spotlight-actions" }, playActions(g, "small", false), link(`/g/${g.id}`, { class: "btn ghost small" }, "Details")),
    ),
  );
}

/** Epic's compact list (launcher `compact_column`): small cover, title, creator, then "Free" and the runtime badge. */
function compactColumn(l: HomeRow): HTMLElement {
  const list = l.games;
  return h(
    "section",
    { class: "compact" },
    h("h2", null, link(rowHref(l.id), null, l.title)),
    h(
      "ul",
      { role: "list" },
      list.slice(0, 5).map((g) =>
        h(
          "li",
          null,
          link(
            `/g/${g.id}`,
            { class: "compact-item" },
            h("span", { class: "thumb", ...lazyCover(g) }),
            h(
              "span",
              { class: "compact-text" },
              h("span", { class: "compact-title" }, g.title),
              h("span", { class: "compact-by" }, creatorName(g.creator.name)),
              h("span", { class: "compact-price" }, isComingSoon(g) ? h("span", { class: "badge warn" }, comingSoonText(g)) : "Free", runtimeBadge(g)),
            ),
          ),
        ),
      ),
    ),
  );
}

function pitch(): HTMLElement {
  return h(
    "section",
    { class: "pitch" },
    h(
      "div",
      null,
      h("h2", null, "Good games are bangers."),
      h("p", null, "One library for the games scattered across the web. Follow the people who make them, see what your friends play, and pick up where you left off."),
    ),
    // phones never lead with a desktop download (design review 19 Oct): there Browse is the button and the app is a
    // text link (style.css swaps them under 700 px)
    h(
      "div",
      { class: "pitch-actions" },
      link("/download", { class: "btn pitch-get" }, icon("download", 16), "Get Banger"),
      link("/browse", { class: "btn ghost pitch-browse" }, "Browse games"),
      link("/download", { class: "pitch-applink" }, "Get the app on your computer"),
    ),
  );
}

/** Kinds a player would look for; examples (developer starters) live on the developers' page and in Browse's sheet. */
export const HOME_KINDS: Kind[] = ["game", "experience", "learn"];

function kinds(cat: HomeCatalogue): HTMLElement | null {
  const counts = HOME_KINDS.map((k) => [k, cat.kindCounts[k] ?? 0] as const).filter(([, n]) => n > 0);
  if (counts.length === 0) return null;
  return h(
    "section",
    { class: "section" },
    h("h2", null, "Find something to play"),
    h(
      "ul",
      { class: "kind-tiles" },
      counts.map(([k, n]) =>
        h("li", null, link(`/browse?kind=${k}`, { class: "kind-tile" }, h("strong", null, KIND_LABEL[k]), h("span", null, `${n}`))),
      ),
    ),
  );
}

/**
 * The featured hero, laid out like the Epic web store's: the hero with the featured list beside it (wide screens; the
 * active row fills left to right as the slide's time runs) and round prev/next arrows under the list, dots too on
 * narrow screens. Auto-advances every 8 s and pauses on hover/focus.
 *
 * Slide changes are the launcher's (launcher/src/ui.rs `home`, TRANS_SECS): the outgoing art slides left and blurs
 * out while its eyebrow, title, blurb and buttons leave on staggered delays, then the incoming art comes in from the
 * right, sharpening, its text following with the same stagger. The timings live in style.css (`.slide.enter`,
 * `.slide.leaving`); TRANS_MS here only says when the outgoing slide can be hidden. It runs whatever the viewer's
 * reduced-motion setting, as the launcher's and Epic's store's do (owner, 2026-10-17).
 */
export function carousel(list: Game[], boosted?: Set<string>): HTMLElement {
  if (list.length === 0) return h("div");
  let index = 0;
  let timer = 0;
  let leaving: { slide: HTMLElement; done: number } | null = null;
  // the text's four parts, which the transition staggers (style.css: `.part:nth-child(n)`)
  const part = (...kids: HTMLElement[]) => h("div", { class: "part" }, ...kids);
  const slides = list.map((g, i) => {
    return h(
      "div",
      {
        class: "slide",
        role: "group",
        "aria-roledescription": "slide",
        "aria-label": `${i + 1} of ${list.length}: ${g.title}`,
        "aria-hidden": i === 0 ? "false" : "true",
      },
      // only the first two slides load their art up front; the rest as the carousel reaches them (`reveal`)
      i < 2 ? h("div", { class: "slide-bg", style: coverStyle(g, "full") }) : h("div", { class: "slide-bg", style: lazyCover(g, "full").style, "data-later": lazyCover(g, "full")["data-bg"] }),
      h(
        "div",
        { class: "slide-text" },
        part(h("p", { class: "eyebrow" }, `Featured · ${label(g.genre)}`, newBadge(g, boosted, "badge new eyebrow-badge"))),
        part(titleArt(g, "slide-title-art", () => h("h2", { class: "slide-title" }, g.title), i >= 2)),
        part(h("p", { class: "slide-blurb" }, g.blurb)),
        part(
          h("div", { class: "slide-actions" }, playActions(g, "big", false), link(`/g/${g.id}`, { class: "btn ghost", tabindex: i === 0 ? null : "-1" }, "Details"), runtimeBadge(g)),
          h("p", { class: "slide-by" }, "by ", link(`/c/${g.creator.id}`, { tabindex: i === 0 ? null : "-1" }, creatorName(g.creator.name))),
        ),
      ),
    );
  });
  const items = list.map((g, i) =>
    h(
      "button",
      { type: "button", class: "feature", "aria-label": `Show ${g.title}`, "aria-current": i === 0 ? "true" : "false", onclick: () => show(i, true) },
      h("span", { class: "feature-fill", "aria-hidden": "true" }),
      h("span", { class: "thumb", style: coverStyle(g) }),
      h("span", { class: "feature-title" }, g.title),
    ),
  );
  const dots = list.map((g, i) =>
    h("button", { class: "dot", type: "button", "aria-label": `Show ${g.title}`, "aria-current": i === 0 ? "true" : "false", onclick: () => show(i, true) }),
  );
  const live = h("div", { class: "sr-only", "aria-live": "polite" });
  const restart = (el: HTMLElement, cls: string) => {
    el.classList.remove(cls);
    void el.offsetWidth; // restart its CSS animations
    el.classList.add(cls);
  };
  const restartFill = () => {
    const fill = items[index]?.querySelector<HTMLElement>(".feature-fill");
    if (!fill) return;
    fill.style.animation = "none";
    void fill.offsetWidth; // restart the CSS animation
    fill.style.animation = "";
  };
  /** load a slide's art (and its title logo) the first time it's about to show */
  const reveal = (i: number) => {
    const slide = slides[(i + list.length) % list.length];
    const bg = slide?.querySelector<HTMLElement>(".slide-bg[data-later]");
    if (bg) {
      bg.style.backgroundImage = `url("${bg.dataset.later}"), ${bg.style.backgroundImage}`;
      bg.removeAttribute("data-later");
    }
    const img = slide?.querySelector<HTMLImageElement>("img[data-src]");
    if (img) {
      img.src = img.dataset.src!;
      img.removeAttribute("data-src");
    }
  };
  const show = (i: number, user = false) => {
    const next = (i + list.length) % list.length;
    if (next === index) return;
    reveal(next);
    reveal(next + 1);
    const from = slides[index]!;
    const to = slides[next]!;
    // one transition at a time, as the launcher's single `trans`: a pick mid-change drops the slide still leaving
    // and the one coming in starts leaving from where it is
    if (leaving) {
      window.clearTimeout(leaving.done);
      leaving.slide.classList.remove("leaving");
    }
    from.classList.remove("on", "enter");
    restart(from, "leaving");
    leaving = { slide: from, done: window.setTimeout(() => (from.classList.remove("leaving"), (leaving = null)), TRANS_MS) };
    to.classList.remove("leaving");
    to.classList.add("on");
    restart(to, "enter");
    index = next;
    slides.forEach((s, j) => {
      s.setAttribute("aria-hidden", j === index ? "false" : "true");
      s.querySelectorAll("a").forEach((a) => (j === index ? a.removeAttribute("tabindex") : a.setAttribute("tabindex", "-1")));
    });
    for (const set of [dots, items]) set.forEach((d, j) => d.setAttribute("aria-current", j === index ? "true" : "false"));
    restartFill();
    if (user) {
      live.textContent = `${list[index]!.title}`;
      if (!root.classList.contains("paused")) start(); // a full slide's time after a pick
    }
  };
  slides[0]!.classList.add("on");
  const stop = () => {
    window.clearInterval(timer);
    root.classList.add("paused");
  };
  const start = () => {
    window.clearInterval(timer);
    if (list.length < 2) return;
    root.classList.remove("paused");
    restartFill();
    timer = window.setInterval(() => {
      if (!root.isConnected) return window.clearInterval(timer);
      show(index + 1);
    }, SLIDE_MS);
  };
  const root: HTMLElement = h(
    "section",
    { class: `carousel${list.length < 2 ? " still" : ""}`, "aria-roledescription": "carousel", "aria-label": "Featured" },
    h(
      "div",
      { class: "hero", onmouseenter: () => stop(), onmouseleave: () => start(), onfocusin: () => stop(), onfocusout: () => start() },
      h("div", { class: "slides" }, slides, h("div", { class: "slides-scrim", "aria-hidden": "true" })),
    ),
    list.length > 1 ? h("div", { class: "features", role: "group", "aria-label": "Featured games" }, items) : null,
    list.length > 1
      ? h(
          "div",
          { class: "carousel-controls" },
          h("div", { class: "dots" }, dots),
          h("button", { class: "round", type: "button", "aria-label": "Previous", onclick: () => show(index - 1, true) }, icon("left", 16)),
          h("button", { class: "round", type: "button", "aria-label": "Next", onclick: () => show(index + 1, true) }, icon("right", 16)),
        )
      : null,
    live,
  );
  root.style.setProperty("--slide-ms", `${SLIDE_MS}ms`);
  root.style.setProperty("--trans-ms", `${TRANS_MS}ms`);
  // swipe on touch screens
  let x0: number | null = null;
  root.addEventListener("touchstart", (e) => (x0 = e.touches[0]?.clientX ?? null), { passive: true });
  root.addEventListener("touchend", (e) => {
    const x1 = e.changedTouches[0]?.clientX;
    if (x0 !== null && x1 !== undefined && Math.abs(x1 - x0) > 40) show(index + (x1 < x0 ? 1 : -1), true);
    x0 = null;
  });
  start();
  return root;
}
